'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiTag } from '@shared/all/domains/factiiis';
import { convertToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import { Loader, Plus, Search } from 'lucide-react';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useInstanceSpaceSlug } from '../../../hooks/useInstanceSpaceSlug';
import { client, type RouterOutputs } from '../../../utils/trpc';
import Button from '../../common/Button';
import Input from '../../UI/Input';
import { FactiiiSearchResult } from '../FactiiiSearchResult';
import CreateSourceFactiii from './CreateSourceFactiii';
type Props = {
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: Dispatch<SetStateAction<FactiiiTag[]>>;
  handleClick: (tag: FactiiiTag) => void;
};
export type SourceItem = RouterOutputs['search']['factiiis']['factiiis'][number] & {
  url?: string;
};
export const SourceFactiii = t0 => {
  const $ = _c(41);
  const {
    myPostFactiiiCounts,
    setMyPostFactiiiCounts,
    handleClick
  } = t0;
  const [createSourceOpen, setCreateSourceOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const preferSpaceSlug = useInstanceSpaceSlug();
  let t1;
  if ($[0] !== preferSpaceSlug || $[1] !== searchQuery) {
    t1 = ["sourceFactiiiSearch", searchQuery, preferSpaceSlug];
    $[0] = preferSpaceSlug;
    $[1] = searchQuery;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== myPostFactiiiCounts || $[4] !== preferSpaceSlug || $[5] !== searchQuery) {
    t2 = () => unwrapFromResult(client.search.factiiis.query({
      query: searchQuery,
      preferSpaceSlug,
      requirements: ["HUMAN_SOURCE", "GOVERNMENT_SOURCE", "ENTERPRISE_SOURCE", "ANONYMOUS_SOURCE"],
      ignoredIds: myPostFactiiiCounts.map(_temp)
    }));
    $[3] = myPostFactiiiCounts;
    $[4] = preferSpaceSlug;
    $[5] = searchQuery;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      placeholderData: _temp2,
      staleTime: 30000
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data: searchResults,
    isLoading: searchLoading
  } = useQuery(t3);
  let t4;
  if ($[10] !== myPostFactiiiCounts || $[11] !== setMyPostFactiiiCounts) {
    t4 = sourceItem => {
      if (myPostFactiiiCounts.some(s_0 => s_0.factiiiId === sourceItem.id)) {
        return alert("BUGZ: Source already added");
      }
      setMyPostFactiiiCounts(prev => [...prev, convertToFactiiiTag(sourceItem)]);
    };
    $[10] = myPostFactiiiCounts;
    $[11] = setMyPostFactiiiCounts;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const addSource = t4;
  let t5;
  if ($[13] !== searchLoading) {
    t5 = [searchLoading ? <Loader key="loading-icon" className="animate-spin" /> : <Search key="search-icon" />];
    $[13] = searchLoading;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== searchQuery || $[16] !== t5) {
    t6 = <Input value={searchQuery} onChange={setSearchQuery} placeholder="Search for sources..." autofocus={true} icons={t5} />;
    $[15] = searchQuery;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== addSource || $[19] !== createSourceOpen || $[20] !== searchQuery) {
    t7 = createSourceOpen && <CreateSourceFactiii factiiiName={searchQuery} closeHandler={() => setCreateSourceOpen(false)} addSource={addSource} />;
    $[18] = addSource;
    $[19] = createSourceOpen;
    $[20] = searchQuery;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== handleClick || $[23] !== myPostFactiiiCounts || $[24] !== searchLoading || $[25] !== searchResults?.factiiis) {
    bb0: {
      let t9;
      if ($[27] !== myPostFactiiiCounts) {
        t9 = new Set(myPostFactiiiCounts.map(_temp3));
        $[27] = myPostFactiiiCounts;
        $[28] = t9;
      } else {
        t9 = $[28];
      }
      const selectedIds = t9;
      const filtered = searchResults?.factiiis?.filter(f => !selectedIds.has(f.id)) ?? [];
      if (filtered.length > 0) {
        let t10;
        if ($[29] !== handleClick) {
          t10 = searchResult => <FactiiiSearchResult key={searchResult.id} tag={convertToFactiiiTag(searchResult)} replyConfig={searchResult.replyConfig} handleClick={() => handleClick(convertToFactiiiTag(searchResult))} />;
          $[29] = handleClick;
          $[30] = t10;
        } else {
          t10 = $[30];
        }
        t8 = filtered.map(t10);
        break bb0;
      }
      if (searchLoading) {
        let t10;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <div className="flex items-center justify-center py-4"><Loader className="animate-spin text-textSecondary" /></div>;
          $[31] = t10;
        } else {
          t10 = $[31];
        }
        t8 = t10;
        break bb0;
      }
      if (searchResults?.factiiis.length === 0 || filtered.length === 0) {
        let t10;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <p className="mb-2 text-center text-sm text-textSecondary">No sources found, click below to add one...</p>;
          $[32] = t10;
        } else {
          t10 = $[32];
        }
        let t11;
        if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = () => setCreateSourceOpen(true);
          $[33] = t11;
        } else {
          t11 = $[33];
        }
        let t12;
        if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = <div className="py-4">{t10}<Button onClick={t11} text="Add New Source Factiii" icon={<Plus />} classNames="w-full" /></div>;
          $[34] = t12;
        } else {
          t12 = $[34];
        }
        t8 = t12;
        break bb0;
      }
      t8 = null;
    }
    $[22] = handleClick;
    $[23] = myPostFactiiiCounts;
    $[24] = searchLoading;
    $[25] = searchResults?.factiiis;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[35] !== t8) {
    t9 = <div className="mt-3 space-y-3">{t8}</div>;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== t6 || $[38] !== t7 || $[39] !== t9) {
    t10 = <div className="flex flex-col">{t6}{t7}{t9}</div>;
    $[37] = t6;
    $[38] = t7;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
};
function _temp(s) {
  return s.factiiiId;
}
function _temp2(previousData) {
  return previousData;
}
function _temp3(s_1) {
  return s_1.factiiiId;
}